import { Link, useLocation } from 'react-router-dom';
import { useState, useRef, useEffect } from 'react';
import {
  NavigationMenu,
  NavigationMenuItem,
  NavigationMenuList,
} from '../../../shared/components/ui/navigation-menu';
import { Badge } from '../../../shared/components/ui/Badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../../shared/components/ui/Select';
import { useOldStrategicPerformanceStore } from '../hooks/useOldStrategicPerformanceStore';
import { useCurrentUser } from '../hooks/useCurrentUser';
import { useAuth } from '../../../contexts/AuthContext';
import { ScorecardPeriodStatus, PerformanceAssessment } from '../types';
import { Target, CheckSquare, User as UserIcon, ClipboardList, FileText, ChevronLeft, ChevronRight } from 'lucide-react';

const Navigation = () => {
  const location = useLocation();
  const [periodStatuses] = useOldStrategicPerformanceStore<ScorecardPeriodStatus[]>('scorecard-period-statuses', []);
  const [performanceAssessments] = useOldStrategicPerformanceStore<PerformanceAssessment[]>('performance-assessments', []);
  const [deptHeadPerformanceAssessments] = useOldStrategicPerformanceStore<PerformanceAssessment[]>('dept-head-performance-assessments', []);
  const { currentUserId, currentUser, switchUser, allUsers } = useCurrentUser();
  const { user } = useAuth();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);

  // Get count of pending scorecard approvals for current user
  const pendingScorecardCount = periodStatuses.filter(
    ps => ps.status === 'submitted' && ps.supervisorId === currentUserId
  ).length;

  // Get count of pending performance assessment approvals for current user (both employee and dept head)
  const allPerformanceAssessments = [...performanceAssessments, ...deptHeadPerformanceAssessments];
  const pendingAssessmentCount = allPerformanceAssessments.filter(
    pa => pa.status === 'submitted' && pa.supervisorId === currentUserId
  ).length;

  // Total pending approvals
  const pendingCount = pendingScorecardCount + pendingAssessmentCount;

  // Check scroll position and update fade indicators
  const checkScrollPosition = () => {
    if (!scrollContainerRef.current) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
    setShowLeftFade(scrollLeft > 0);
    setShowRightFade(scrollLeft < scrollWidth - clientWidth - 1);
  };

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    checkScrollPosition();
    container.addEventListener('scroll', checkScrollPosition);
    window.addEventListener('resize', checkScrollPosition);

    // Check on mount and after a short delay to account for layout
    const timeoutId = setTimeout(checkScrollPosition, 100);

    return () => {
      container.removeEventListener('scroll', checkScrollPosition);
      window.removeEventListener('resize', checkScrollPosition);
      clearTimeout(timeoutId);
    };
  }, []);

  const scroll = (direction: 'left' | 'right') => {
    if (!scrollContainerRef.current) return;
    const scrollAmount = 200;
    const currentScroll = scrollContainerRef.current.scrollLeft;
    scrollContainerRef.current.scrollTo({
      left: direction === 'left' ? currentScroll - scrollAmount : currentScroll + scrollAmount,
      behavior: 'smooth'
    });
  };

  return (
    <header className="sticky top-0 z-50 w-full border-b border-border bg-card shadow-sm">
      <div className="relative">
        {/* Left fade gradient */}
        {showLeftFade && (
          <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-card to-transparent pointer-events-none z-10" />
        )}
        
        {/* Right fade gradient */}
        {showRightFade && (
          <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-card to-transparent pointer-events-none z-10" />
        )}

        {/* Scroll buttons */}
        {showLeftFade && (
          <button
            onClick={() => scroll('left')}
            className="absolute left-2 top-1/2 -translate-y-1/2 z-20 h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm border border-border shadow-sm hover:bg-background hover:shadow-md transition-all flex items-center justify-center"
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-4 w-4 text-foreground" />
          </button>
        )}
        
        {showRightFade && (
          <button
            onClick={() => scroll('right')}
            className="absolute right-2 top-1/2 -translate-y-1/2 z-20 h-8 w-8 rounded-full bg-background/80 backdrop-blur-sm border border-border shadow-sm hover:bg-background hover:shadow-md transition-all flex items-center justify-center"
            aria-label="Scroll right"
          >
            <ChevronRight className="h-4 w-4 text-foreground" />
          </button>
        )}

        <div 
          ref={scrollContainerRef}
          className="nav-scroll-container mx-auto px-4 overflow-x-auto scroll-smooth"
          style={{ scrollbarWidth: 'thin' }}
        >
          <div className="flex h-16 items-center justify-between gap-4 min-w-max py-2">
          <div className="flex items-center space-x-2 flex-shrink-0">
          </div>

          {/* {allUsers.length > 1 && (
          <div className="flex items-center gap-4 flex-shrink-0">
            <div className="flex items-center gap-2 px-3 py-1.5 rounded-md bg-secondary/50">
              <UserIcon className="h-4 w-4 text-muted-foreground" />
              <Select value={currentUserId} onValueChange={switchUser}>
                <SelectTrigger className="h-8 border-0 bg-transparent focus:ring-0 focus:ring-offset-0">
                  <SelectValue>
                    <span className="text-sm font-medium">
                      {currentUser?.fullName || 'Select User'}
                    </span>
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-popover z-[60]">
                  {allUsers.map((user) => (
                    <SelectItem key={user.id} value={user.id}>
                      <div className="flex flex-col">
                        <span className="font-medium">{user.fullName}</span>
                        <span className="text-xs text-muted-foreground">{user.jobTitle}</span>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          )} */}

          <NavigationMenu className="flex-shrink-0">
              <NavigationMenuList className="flex-nowrap whitespace-nowrap gap-1">
              {(user?.isStrategicPerformanceSuperUser || (user?.jobTitle && user.jobTitle.toLowerCase().includes('ceo'))) && (
                <NavigationMenuItem>
                  <Link 
                    to="/old-strategic-performance/reports" 
                    className={`inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none disabled:pointer-events-none disabled:opacity-50 ${
                      location.pathname.startsWith('/old-strategic-performance/reports')
                        ? 'bg-primary/10 text-primary border-b-2 border-primary'
                        : 'bg-transparent hover:bg-secondary hover:text-accent-foreground focus:bg-secondary focus:text-accent-foreground'
                    }`}
                  >
                    <FileText className="h-4 w-4 mr-2" />
                    Reports
                  </Link>
                </NavigationMenuItem>
              )}

              <NavigationMenuItem>
                <Link 
                  to="/old-strategic-performance/approvals" 
                    className={`inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none disabled:pointer-events-none disabled:opacity-50 ${
                      location.pathname.startsWith('/old-strategic-performance/approvals')
                        ? 'bg-primary/10 text-primary border-b-2 border-primary'
                        : 'bg-transparent hover:bg-secondary hover:text-accent-foreground focus:bg-secondary focus:text-accent-foreground'
                    }`}
                >
                  <CheckSquare className="h-4 w-4 mr-2" />
                  Approvals
                  <Badge 
                    variant="secondary" 
                    className={`ml-2 ${
                      pendingCount === 0 
                        ? 'bg-green-500/20 text-green-700 dark:text-green-400' 
                        : 'bg-red-500/20 text-red-700 dark:text-red-400'
                    }`}
                  >
                    {pendingCount}
                  </Badge>
                </Link>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <Link 
                  to="/old-strategic-performance/performance-assessments" 
                    className={`inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none disabled:pointer-events-none disabled:opacity-50 ${
                      location.pathname.startsWith('/old-strategic-performance/performance-assessments') || location.pathname.startsWith('/old-strategic-performance/dept-head-performance-assessments')
                        ? 'bg-primary/10 text-primary border-b-2 border-primary'
                        : 'bg-transparent hover:bg-secondary hover:text-accent-foreground focus:bg-secondary focus:text-accent-foreground'
                    }`}
                >
                  <ClipboardList className="h-4 w-4 mr-2" />
                  Performance Assessments
                </Link>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <Link 
                  to="/old-strategic-performance/employee-scorecards" 
                    className={`inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none disabled:pointer-events-none disabled:opacity-50 ${
                      location.pathname.startsWith('/old-strategic-performance/employee-scorecards')
                        ? 'bg-primary/10 text-primary border-b-2 border-primary'
                        : 'bg-transparent hover:bg-secondary hover:text-accent-foreground focus:bg-secondary focus:text-accent-foreground'
                    }`}
                >
                  Employee Scorecards
                </Link>
              </NavigationMenuItem>

              {(user?.isStrategicPerformanceSuperUser || currentUser?.isCEO) && (
                <NavigationMenuItem>
                  <Link 
                    to="/old-strategic-performance/organization-performance" 
                    className={`inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none disabled:pointer-events-none disabled:opacity-50 ${
                      location.pathname.startsWith('/old-strategic-performance/organization-performance')
                        ? 'bg-primary/10 text-primary border-b-2 border-primary'
                        : 'bg-transparent hover:bg-secondary hover:text-accent-foreground focus:bg-secondary focus:text-accent-foreground'
                    }`}
                  >
                    Organization Performance
                  </Link>
                </NavigationMenuItem>
              )}

              <NavigationMenuItem>
                <Link 
                  to="/old-strategic-performance/annual-corporate-scorecards" 
                    className={`inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none disabled:pointer-events-none disabled:opacity-50 ${
                      location.pathname.startsWith('/old-strategic-performance/annual-corporate-scorecards')
                        ? 'bg-primary/10 text-primary border-b-2 border-primary'
                        : 'bg-transparent hover:bg-secondary hover:text-accent-foreground focus:bg-secondary focus:text-accent-foreground'
                  }`}
                >
                  Annual Corporate Scorecards
                </Link>
              </NavigationMenuItem>

              <NavigationMenuItem>
                <Link 
                  to="/old-strategic-performance/strategic-plans" 
                    className={`inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none disabled:pointer-events-none disabled:opacity-50 ${
                      location.pathname.startsWith('/old-strategic-performance/strategic-plans')
                        ? 'bg-primary/10 text-primary border-b-2 border-primary'
                        : 'bg-transparent hover:bg-secondary hover:text-accent-foreground focus:bg-secondary focus:text-accent-foreground'
                  }`}
                >
                  Strategic Plans
                </Link>
              </NavigationMenuItem>

              {user?.isStrategicPerformanceSuperUser && (
                <NavigationMenuItem>
                  <Link 
                    to="/old-strategic-performance/settings" 
                    className={`inline-flex h-10 w-max items-center justify-center rounded-md px-4 py-2 text-sm font-medium transition-colors focus:outline-none disabled:pointer-events-none disabled:opacity-50 ${
                      location.pathname.startsWith('/old-strategic-performance/settings')
                        ? 'bg-primary/10 text-primary border-b-2 border-primary'
                        : 'bg-transparent hover:bg-secondary hover:text-accent-foreground focus:bg-secondary focus:text-accent-foreground'
                    }`}
                  >
                    Settings
                  </Link>
                </NavigationMenuItem>
              )}
            </NavigationMenuList>
          </NavigationMenu>
          </div>
        </div>
      </div>
      <style>{`
        .nav-scroll-container {
          scrollbar-width: thin;
          scrollbar-color: hsl(var(--primary) / 0.3) hsl(var(--muted));
        }
        
        .nav-scroll-container::-webkit-scrollbar {
          height: 10px;
        }
        
        .nav-scroll-container::-webkit-scrollbar-track {
          background: hsl(var(--muted) / 0.5);
          border-radius: 10px;
          margin: 0 4px;
        }
        
        .nav-scroll-container::-webkit-scrollbar-thumb {
          background: hsl(var(--primary) / 0.4);
          border-radius: 10px;
          border: 2px solid hsl(var(--muted) / 0.5);
          transition: background 0.2s ease;
        }
        
        .nav-scroll-container::-webkit-scrollbar-thumb:hover {
          background: hsl(var(--primary) / 0.6);
        }
        
        .nav-scroll-container::-webkit-scrollbar-thumb:active {
          background: hsl(var(--primary));
        }
      `}</style>
    </header>
  );
};

export default Navigation;
